/**
 * BeckChevalleyExplainer — the Beck-Chevalley condition, its failure, and
 * the structural identification "BC failure ⇔ CSK seam".
 *
 * In a pullback square whose vertical edges carry adjunctions, there is a
 * canonical mate 2-cell. When the mate is an isomorphism the square is
 * well-behaved — the corresponding "free crossing" is structurally
 * available and no governance is needed. When the mate is not an
 * isomorphism — the regime CSK lives in for every governed seam —
 * the free crossing is unconstructable, and any crossing must be a
 * restricted (witness-carrying) Kan extension datum, i.e. a bridge.
 *
 * The card opens at a no-notation TL;DR and climbs through Intuition,
 * Formal, CSK relevance, and References. Two side-by-side BC squares
 * (one holds, one fails) ride in the diagram slot.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { COMPOSITIONALITY_PRINCIPLE_COLOR } from "../../design-system/tokens.js";
import { MathBlock, MathInline, tex } from "../../components/math/index.js";
import {
  BeckChevalleySquareDiagram,
  ExplainerCard,
  ModeToggle,
  SeamBridgeKanDiagram,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";

const ACCENT = COMPOSITIONALITY_PRINCIPLE_COLOR.ALWAYS.color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-beck-chevalley",
  kind: "Categorical structure",
  title: "Beck-Chevalley condition (and its failure)",
  subtitle: "Σ_g ∘ f  ≅  k ∘ Σ_h   —   when the mate is not an iso, the square is a seam",
  accent: ACCENT,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  { label: "Adjunction", href: "#explainer-adjunction", accent: ACCENT },
  { label: "Triangle identities", href: "#explainer-triangle-identities", accent: ACCENT },
  { label: "Higher / graded adjunctions", href: "#explainer-higher-adjunctions", accent: ACCENT },
  { label: "Scope · Effect · Coeffect", href: "#explainer-scope-effect-coeffect" },
  { label: "ALWAYS (SDP)", href: "#explainer-always", accent: ACCENT },
  { label: "INV.CONSISTENCY (DPG)", href: "#explainer-inv-consistency" },
];

// inline: local
const BC_TOGGLE_OPTIONS = [
  { mode: "fails" as const, label: "BC fails (seam)" },
  { mode: "holds" as const, label: "BC holds (free crossing)" },
] as const;

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      The <strong>Beck-Chevalley condition</strong> asks whether two ways of moving data across a
      pullback square — restrict-then-aggregate versus aggregate-then-restrict — agree. When they
      agree, data flows freely across the square with no governance needed. When they disagree — the
      regime CSK lives in for every governed boundary — you have a <em>seam</em>: the free crossing
      is structurally unconstructable, and any crossing must carry an explicit witness — a{" "}
      <em>bridge</em>.
    </p>
  ),
  intuition: (
    <>
      <p>
        Picture two compliance domains — <strong>Production</strong> and <strong>Staging</strong>.
        Each carries its own policy: "data here must be signed", "data here may be unsigned",
        "events here may be aggregated", and so on. A pullback square is the formal record of two
        ways to mix data between the two domains:
      </p>
      <ul>
        <li>
          <strong>Restrict, then aggregate.</strong> Start with all events in the wider domain;
          filter to the Production-valid ones; then aggregate (sum, union, sequence) what remains.
        </li>
        <li>
          <strong>Aggregate, then restrict.</strong> Aggregate first, then filter to
          Production-valid.
        </li>
      </ul>
      <p>
        The Beck-Chevalley condition asks: do these two routes give the same answer? In ordinary
        well-behaved settings — same domain, no governance — yes.{" "}
        <em>Across a governance seam, no</em>: aggregating staging evidence and then "restricting"
        to production policy could let an attacker smuggle weak evidence past a strong policy. The
        whole point of the seam is that this composition must not exist freely.
      </p>
      <p>
        So CSK turns the failure into a feature. A seam is, exactly, a BC failure. The free crossing
        is structurally inexpressible; the only crossing that types-check is a <em>bridge</em>: a
        constrained, witnessed crossing that explicitly carries the justification an auditor will
        demand later. (Think capability grant, declassification certificate, precommit evidence,
        budget allocation.)
      </p>
      <p>
        That is why "BC failure ⇔ seam" is more than a slogan: the algebraic failure of the mate
        2-cell to be invertible is the mechanism by which authority laundering, retroactive
        justification, and unbounded aggregation are made unconstructable.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        Consider a pullback square in <code>Cat</code> with adjunctions on the vertical edges:
      </p>
      <ModeToggle
        options={BC_TOGGLE_OPTIONS}
        defaultMode="fails"
        accent={ACCENT}
        ariaLabel="Switch between BC holds and BC fails"
      >
        {(mode) => <BeckChevalleySquareDiagram mode={mode} accent={ACCENT} />}
      </ModeToggle>
      <p style={{ marginTop: "0.75rem" }}>
        Given <MathInline>{tex`\Sigma_{h} \dashv h`}</MathInline> and{" "}
        <MathInline>{tex`\Sigma_{g} \dashv g`}</MathInline>, the canonical <strong>mate</strong>{" "}
        2-cell is the composite
      </p>
      <MathBlock ariaLabel="Σ_g ∘ f ⇒ Σ_g ∘ f ∘ h ∘ Σ_h ⇒ Σ_g ∘ g ∘ k ∘ Σ_h ⇒ k ∘ Σ_h">
        {tex`\Sigma_{g} \circ f \;\Rightarrow\; \Sigma_{g} \circ f \circ h \circ \Sigma_{h} \;\Rightarrow\; \Sigma_{g} \circ g \circ k \circ \Sigma_{h} \;\Rightarrow\; k \circ \Sigma_{h}`}
      </MathBlock>
      <p>
        built by inserting the unit of <MathInline>{tex`\Sigma_{h} \dashv h`}</MathInline>, sliding
        the square (using the pullback property), and contracting via the counit of{" "}
        <MathInline>{tex`\Sigma_{g} \dashv g`}</MathInline>. The square satisfies the{" "}
        <strong>Beck-Chevalley condition</strong> iff this mate is an isomorphism — equivalently,
        iff <MathInline>{tex`\Sigma`}</MathInline>-aggregation commutes with the
        pullback-restriction in the strongest possible sense.
      </p>
      <p>Two well-named subtheorems sit underneath:</p>
      <ul>
        <li>
          <strong>RAPL</strong> (Right Adjoints Preserve Limits) — the right edge of the BC square
          preserves the pullback because <MathInline>{tex`g`}</MathInline> is a right adjoint.
        </li>
        <li>
          <strong>LAPC</strong> (Left Adjoints Preserve Colimits) — the left edge preserves
          pushforwards because <MathInline>{tex`\Sigma_{h}`}</MathInline> is a left adjoint.
        </li>
      </ul>
      <p>
        The BC condition is exactly the statement that RAPL on the right and LAPC on the left
        compose into the same 2-cell. When they do not — when CSK refuses the free composite for
        security reasons — the square is a <strong>seam</strong>, and any crossing is realized as a{" "}
        <strong>restricted Kan extension datum</strong>: a constrained, adoption-gated witness that
        the auditor can later inspect.
      </p>
      <SeamBridgeKanDiagram accent={ACCENT} />
    </>
  ),
  relevance: (
    <>
      <p>
        Beck-Chevalley failure is the algebraic phenomenon CSK exploits to make four universal
        forbidden patterns structurally inexpressible. Each pattern is a BC failure of a different
        flavor:
      </p>
      <ul>
        <li>
          <strong>G1 (Ambient Effect)</strong> — effect without authority. A free mate from "effect
          already happened" to "authority granted" would let arbitrary effects acquire authority
          retroactively. BC failure on the authority fibration neutralizes this by construction.
        </li>
        <li>
          <strong>G2 (Epistemic Lift)</strong> — information ⇒ authority. A free mate from "label
          known" to "permission held" would equate knowing with permission. BC failure on the
          knowledge / authority fibration eliminates the lift.
        </li>
        <li>
          <strong>G3 (Time Reversal)</strong> — retroactive justification. A free mate from
          accounting evidence back to authorization input would let an event justify itself after
          the fact. The non-feedback invariant in Seam Normal Form (B4 ↛ B2) is precisely this BC
          failure.
        </li>
        <li>
          <strong>G4 (Authority Duplication)</strong> — copying inflates power. A free mate from
          "one capability" to "two capabilities" would let authority be duplicated by aggregation.
          BC failure on the resource fibration keeps authority scarce.
        </li>
      </ul>
      <p>
        Concretely: every CSK seam declared by a Neutralization Theorem is a specific BC failure at
        a specific pullback square. Every CSK bridge is a specific{" "}
        <em>restricted Kan extension datum</em> at that same square. The thickness of the bridge —
        its witness-carrying nature — is exactly what makes the failure usable for security rather
        than a mere obstruction.
      </p>
      <p>
        <strong>Real-world payoff.</strong> When a security engineer asks "can data flow from
        Staging to Production?", the algebraic answer is "only via a bridge, because the BC mate
        fails". When the same engineer asks "what does the bridge demand?", the answer is "the
        explicit, consumed-on-use witness packaged by the corresponding restricted Kan datum". No
        conventions; no after-the-fact policies. The discipline is the structure.
      </p>
    </>
  ),
  references: [
    {
      label: "Seam theory",
      path: "theory/structural/SEAM-THEORY.md",
      note: "BC failure is the structural identification of every CSK seam.",
    },
    {
      label: "Pullback / Pushout / BC / Adjunction framework",
      path: "theory/structural/Pullback-Pushout-BC-Adjunction-Framework.md",
      note: "Unifies seams, BC failure, and constrained Kan extensions in one story.",
    },
    {
      label: "BECK-CHEVALLEY-FAILURE.md",
      path: "theory/structural/BECK-CHEVALLEY-FAILURE.md",
      note: "Fibration-level treatment of BC failure as an obstruction.",
    },
    {
      label: "BeckChevalley.agda (re-export hub)",
      path: "agda/MathLib/CategoryTheory/BeckChevalley.agda",
      note: "Mechanized BC condition, mate construction, and failure witnesses.",
    },
    {
      label: "MateSeam.agda (Foundation/BeckChevalley/CategoriesLib)",
      path: "agda/Foundation/BeckChevalley/CategoriesLib/MateSeam.agda",
      note: "Constructive mate-of-an-adjunction as the seam witness.",
    },
    {
      label: "Constrained Kan extension datum (MathLibCore)",
      path: "agda/MathLibCore/KanExtension/Core.agda",
      note: "RestrictedDistributiveBridge — the bridge as an adoption-gated Kan datum.",
    },
  ],
};

export function BeckChevalleyExplainer(): React.JSX.Element {
  return (
    <ExplainerCard
      identity={IDENTITY}
      tiers={TIERS}
      diagram={<BeckChevalleySquareDiagram mode="fails" accent={ACCENT} />}
      crossLinks={CROSS_LINKS}
    />
  );
}
